import util from "../../lib/util";
import store from "../../store/store";
import {getOptionDefaults} from './optionDefaults';
import * as OptionActions from '../../store/actions/optionActions';
import Option from '../../entities/option';
import {deleteOption} from '../../store/deps/optionDeps';
import {getCurrentFirmId, getCurrentUserId} from "../../lib/picos/currentLogin";
import {genOptionUUID} from "../../lib/picos/uuids";

class OptionHelper {
    // Constants for option names
    static BILLING_INCREMENT = 'billingIncrement';
    static EMAIL_CLIENT = 'paymentEmailClient';
    static EMAIL_PAYER = 'paymentEmailPayer';
    static EMAIL_SELF = 'paymentEmailSelf';
    static FONT_FAMILY = 'invoiceFontFamily';
    static FONT_SIZE = 'invoiceFontSize';
    static FIRM_ADDRESS = 'invoiceFirmAddress';
    static FIRM_LETTERHEAD = 'invoiceFirmLetterhead';
    static LETTERHEAD_WIDTH = 'invoiceLetterheadWidth';
    static PAID_CONSULTS = 'paidConsults';
    static PAID_CONSULT_PRICE = 'paidConsultPrice';
    static PAID_CONSULT_DURATION = 'paidConsultDuration';
    static PAID_CONSULT_NAME = 'paidConsultName';
    static PAID_CONSULT_ACTIVITY = 'paidConsultActivityId';
    static LEAD_FOLLOWUP_ACTIVITY = 'leadFollowupActivityId';
    static LEAD_INITIAL_CONSULT_USER = 'leadInitialConsultUser';
    static USE_CASE_TRUST = 'useCaseTrust';
    static INVOICE_DEFAULT_SUMMARY = 'invoiceDefaultSummary';
    static INVOICE_NARRATIVE_HEADER = 'invoiceNarrativeHeader';
    static INVOICE_TERMS = 'invoiceTerms';
    static ENFORCE_ACTIVITY_TYPE = 'enforceActivityType';
    static ENFORCE_CATEGORY_SELECTED = 'enforceCategorySelected';
    static ENFORCE_TIE_INS_COMPLETE = 'enforceTieInsComplete';
    static ROUNDING_METHOD = 'roundingMethod';
    static SHOW_HELP = 'showHelp';
    static SOUND_FOR_WIN = 'soundForWin';
    static SOUND_FOR_TASK_COMPLETE = 'soundForTaskComplete';
    static TASK_EMOJIS = 'taskEmojis';

    /*** Tax & Interest ***/
    static INVOICE_TAX_RATE = 'invoiceTaxRate';
    static INVOICE_TAX_LABEL = 'invoiceTaxLabel';
    static INVOICE_INTEREST_RATE = 'invoiceInterestRate';
    static INVOICE_INTEREST_METHOD = 'invoiceInterestMethod';
    static INVOICE_INTEREST_GRACE_PERIOD = 'invoiceInterestGracePeriod';
    static INVOICE_LEAD_PERIOD = 'invoiceLeadPeriod';

    /*** Calendar (OWA-1313) ***/
    static CALENDAR_ENFORCE_WALLS = 'calendarEnforceWalls';
    static CALENDAR_EVENT_KINDS = 'calendarEventKinds';

    /*** Payment Portal ***/
    static PAYMENT_PORTAL = 'paymentPortal';
    static PAYMENT_PORTAL_BENEFACTORS_LINK = 'paymentPortalBenefactorsLink';
    static PAYMENT_PORTAL_BENEFACTORS_INVOICE = 'paymentPortalBenefactorsInvoice';

    // One table of defaults, shared with useOptions (OWA-1586)
    static getDefaultsForLabel = (label: string): {defaultValue: any, type: string} => getOptionDefaults(label);

    /**
     * Get an option based on its label.
     *
     * @param {string} label
     * @return {Option}
     */
    static getOptionByLabel = (label: string): Option => {
        // Get defaults for this label
        const {defaultValue, type} = OptionHelper.getDefaultsForLabel(label);

        // See if the option already exists
        const result = util.sortEntityArrayByDate(store.getState().options
            .getMappingFor('label', label), 'addedOn', 'desc')[0];

        if (!result) {
            // See if it exists in defaultEntries section
            return Option.getDefaultEntries().find(o => o.label === label) || new Option({
                optionId: genOptionUUID(getCurrentFirmId(), label),
                firmId: getCurrentFirmId(),
                addedBy: getCurrentUserId(),
                addedOn: 0.1,
                status: 'C',
                userScope: false,
                label,
                type,
                value: (defaultValue != null)
                       ? type === 'string'
                         ? defaultValue
                         : type === 'boolean'
                           ? defaultValue === true || defaultValue === 'true' || defaultValue === 't'
                           : `${defaultValue}`
                       : null,

            });
        }
        else return result;
    }

    getOptionByLabel = (label: string) => {
        return OptionHelper.getOptionByLabel(label);
    }

    /**
     * Get the value of the option.
     *
     * @param {string} label
     * @return {string|number|boolean|null}
     */
    getOptionValue = <T = any>(label: string): T => {
        try {
            const option = this.getOptionByLabel(label);
            const value = option.value;

            return (option.type === 'numeric'
                   ? Number.parseFloat(value)
                   : option.type === 'boolean'
                     ? typeof value === 'string'
                       ? value.toLowerCase().startsWith('t')
                       : value
                     : value) as T;
        }
        catch (err: any) {
            console.log(err.message);
            return null;
        }
    }

    /**
     * Static version (optimized) of getOptionValue.
     *
     * @param {string} label
     * @return {string|number|boolean|null}
     */
    static getOptionValueStatic = (label: string): string | number | boolean | null => {
        const option = this.getOptionByLabel(label);
        const value = option.value;

        try {
            if (option.type === 'numeric') {
                try {
                    return Number.parseFloat(value)
                }
                catch (err: any) {
                    console.warn(err.message);
                    return null;
                }
            }
            else if (option.type === 'boolean') {
                if (typeof value === 'string') {
                    try {
                        return value.toLowerCase().startsWith('t')
                    }
                    catch (err: any) {
                        console.warn(err.message);
                        return null;
                    }
                }
                else return value;
            }
            else return value;
        }
        catch (err: any) {
            console.log(err.message);
            return null;
        }
    }

    /**
     * Set an option by label/value and optional type.
     * @param {string} label
     * @param {string|number|boolean} value
     * @param {string} [type]
     * @returns {Option}
     */
    setOption = (label: string, value: string | number | boolean, type: string = 'string'): Option => {
        // First see if the option exists already
        let option: Option;

        try {
            option = this.getOptionByLabel(label);
        }
        catch (err) {
            // Safe to ignore
        }

        if (!option) option = new Option({
            firmId: getCurrentFirmId(),
            optionId: genOptionUUID(getCurrentFirmId(), label),
            addedBy: getCurrentUserId(),
            addedOn: util.newHRTimestamp(),
            type, label, value
        });
        else {
            // Save a new version; never change the stored option before its write lands
            option = option.clone();
            option.addedBy = getCurrentUserId();
            option.addedOn = util.newHRTimestamp();
            option.value = `${value}`;
            // A save over a deleted option brings it back: a deleted status would make the client drop the
            // new value and the queue send the save as a remove (OWA-1297)
            if (option.status === 'D') option.status = 'C';
        }

        // Now save it, as a plain save even when cloned from a queued restore. The server appends
        // after a delete of the same ID (OWA-1294).
        option.restore = false;
        store.dispatch(OptionActions.saveOptionQueue(option));

        return option;
    }

    /**
     * Delete an option in its entirety.
     *
     * @param {string} label
     */
    deleteOptionByLabel = (label: string) => {
        const option = this.getOptionByLabel(label);

        // Delete a copy: the delete stamps its option, and the stored one must not change before its write lands
        if (option) deleteOption(option.clone());
    }
}

export default OptionHelper;
